import {useQuery} from '@tanstack/react-query';
import {
  countPiecesInFen,
  TABLEBASE_MAX_PIECES,
  TablebaseCategory,
  TablebaseMove,
} from '@/Screens/Study/utils/explorerUtils';
import {ExplorerError} from './useOpeningExplorer';

/**
 * Syzygy 7-man endgame tablebase via Lichess
 * (https://github.com/lichess-org/lila-tablebase). Only queried when the
 * position has at most 7 men — the caller doesn't need to gate it.
 *
 * Unlike the opening explorer, tablebase.lichess.ovh is a public, unauthenticated
 * endpoint - AND its CORS preflight response does not list `Authorization` in
 * Access-Control-Allow-Headers (explorer.lichess.ovh's does). Sending the
 * explorer's Bearer token here doesn't just go unused: it makes the browser's
 * preflight fail and blocks every request outright (fetch throws, surfaced
 * here as an ExplorerError('network', ...)), which is why this used to show
 * "Tablebase is busy" permanently even though the position was reachable
 * fine outside the browser (e.g. via curl). Do not add auth headers here.
 */

export type TablebaseData = {
  category: TablebaseCategory;
  dtz: number | null;
  dtm: number | null;
  checkmate: boolean;
  stalemate: boolean;
  insufficient_material: boolean;
  moves: TablebaseMove[];
};

export default function useTablebase(
  fen: string | undefined | null,
  enabled = true,
) {
  const withinTablebase =
    !!fen && countPiecesInFen(fen) <= TABLEBASE_MAX_PIECES;

  return useQuery<TablebaseData, ExplorerError>(
    ['lichessTablebase', fen],
    async ({signal}) => {
      let res: Response;
      try {
        res = await fetch(
          `https://tablebase.lichess.ovh/standard?fen=${encodeURIComponent(
            fen as string,
          )}`,
          {signal},
        );
      } catch (networkErr) {
        throw new ExplorerError('network', undefined, String(networkErr));
      }
      if (!res.ok) {
        if (res.status === 401 || res.status === 403) {
          throw new ExplorerError('auth', res.status);
        }
        if (res.status === 429) {
          throw new ExplorerError('rate-limit', 429);
        }
        throw new ExplorerError('server', res.status);
      }
      return res.json();
    },
    {
      enabled: enabled && withinTablebase,
      staleTime: Infinity,
      keepPreviousData: true,
      retry: (failureCount, error: unknown) => {
        if (error instanceof ExplorerError) {
          if (error.kind === 'auth') {
            return false;
          }
          if (error.kind === 'rate-limit') {
            return failureCount < 2;
          }
          return failureCount < 2;
        }
        return false;
      },
      retryDelay: (_, error: unknown) => {
        if (error instanceof ExplorerError && error.kind === 'rate-limit') {
          return 61_000;
        }
        return 1_000;
      },
    },
  );
}
